import { ViewDensity, ViewType } from "@common/types/views/views";
import {
    GeneralLedgerColumn,
    GeneralLedgerView,
} from "@common/types/views/generalLedgerView";
import { NumberFormatValues } from "@common/schemas/domains/reportSettings/number-format-setting.schema";
import { formatMoneyColumnHeader } from "@common/helpers/number";
import { ViewConfig } from "@lib/views";
import { AMOUNT_COLUMN_SIZE } from "@design-system/ViewTable/DefaultColumnSizes";
import {
    GLAmountCell,
    GLBalanceCell,
    GLClassCell,
    GLCounterpartyCell,
    GLDescriptionCell,
    GLEntityCell,
    GLExcludeFrom1099Cell,
    GLMemoCell,
    GLNameCell,
    GLReferenceCell,
    GLSourceCell,
    GLSplitCell,
} from "./Cells";
import { GLViewRow } from "./glViewTypes";

// ---------------------------------------------------------------------------
// Column sizing — columns without a shared constant
// ---------------------------------------------------------------------------

const SOURCE_MIN_SIZE = 120;
const REFERENCE_MIN_SIZE = 120;
const DESCRIPTION_MIN_SIZE = 200;
const MEMO_MIN_SIZE = 200;
const ENTITY_COLUMN_SIZE = 100;
const COUNTERPARTY_MIN_SIZE = 140;
const CLASS_MIN_SIZE = 100;
const SPLIT_MIN_SIZE = 100;
const EXCLUDE_FROM_1099_SIZE = 140;
const DEFAULT_MAX_SIZE = 400;

// ---------------------------------------------------------------------------
// Public builders
// ---------------------------------------------------------------------------

export function buildGLConfig(opts: {
    multiEntity: boolean;
    numberFormat?: NumberFormatValues;
    isClassesEnabled: boolean;
}): ViewConfig<GLViewRow, string, never> {
    const amountHeader = formatMoneyColumnHeader("Amount", opts.numberFormat);
    const balanceHeader = formatMoneyColumnHeader("Balance", opts.numberFormat);
    return {
        columns: {
            [GeneralLedgerColumn.NAME]: {
                name: "Account / Date",
                header: "Date",
                accessorKey: "rowKind",
                cell: GLNameCell,
                minSize: 400,
                maxSize: 600,
                immutable: true,
            },
            [GeneralLedgerColumn.SOURCE]: {
                name: "Source",
                header: "Source",
                accessorKey: "rowKind",
                cell: GLSourceCell,
                minSize: SOURCE_MIN_SIZE,
                maxSize: DEFAULT_MAX_SIZE,
            },
            [GeneralLedgerColumn.REFERENCE]: {
                name: "Reference",
                header: "Reference",
                accessorKey: "rowKind",
                cell: GLReferenceCell,
                minSize: REFERENCE_MIN_SIZE,
                maxSize: DEFAULT_MAX_SIZE,
            },
            [GeneralLedgerColumn.DESCRIPTION]: {
                name: "Description",
                header: "Description",
                accessorKey: "rowKind",
                cell: GLDescriptionCell,
                minSize: DESCRIPTION_MIN_SIZE,
                maxSize: DEFAULT_MAX_SIZE,
            },
            [GeneralLedgerColumn.MEMO]: {
                name: "Memo",
                header: "Memo",
                accessorKey: "rowKind",
                cell: GLMemoCell,
                minSize: MEMO_MIN_SIZE,
                maxSize: DEFAULT_MAX_SIZE,
            },
            [GeneralLedgerColumn.ENTITY]: {
                name: "Entities",
                header: "Entities",
                accessorKey: "rowKind",
                cell: GLEntityCell,
                minSize: ENTITY_COLUMN_SIZE,
                maxSize: ENTITY_COLUMN_SIZE,
                hiddenFromSettings: !opts.multiEntity,
            },
            [GeneralLedgerColumn.COUNTERPARTY]: {
                name: "Counterparty",
                header: "Counterparty",
                accessorKey: "rowKind",
                cell: GLCounterpartyCell,
                minSize: COUNTERPARTY_MIN_SIZE,
                maxSize: DEFAULT_MAX_SIZE,
            },
            ...(opts.isClassesEnabled
                ? {
                      [GeneralLedgerColumn.CLASS]: {
                          name: "Class",
                          header: "Class",
                          accessorKey: "rowKind",
                          cell: GLClassCell,
                          minSize: CLASS_MIN_SIZE,
                          maxSize: DEFAULT_MAX_SIZE,
                      },
                  }
                : {}),
            [GeneralLedgerColumn.SPLIT]: {
                name: "Split",
                header: "Split",
                accessorKey: "rowKind",
                cell: GLSplitCell,
                minSize: SPLIT_MIN_SIZE,
                maxSize: DEFAULT_MAX_SIZE,
            },
            [GeneralLedgerColumn.EXCLUDE_FROM_1099]: {
                name: "Exclude from 1099",
                header: "Exclude from 1099",
                accessorKey: "rowKind",
                cell: GLExcludeFrom1099Cell,
                minSize: EXCLUDE_FROM_1099_SIZE,
                maxSize: EXCLUDE_FROM_1099_SIZE,
            },
            [GeneralLedgerColumn.AMOUNT]: {
                name: "Amount",
                header: amountHeader,
                accessorKey: "rowKind",
                cell: GLAmountCell,
                ...AMOUNT_COLUMN_SIZE,
                immutable: true,
            },
            [GeneralLedgerColumn.BALANCE]: {
                name: "Balance",
                header: balanceHeader,
                accessorKey: "rowKind",
                cell: GLBalanceCell,
                ...AMOUNT_COLUMN_SIZE,
                immutable: true,
            },
        },
        nesting: {
            enabled: true,
            expandColumn: GeneralLedgerColumn.NAME,
            childrenKey: "children",
        },
    };
}

export function buildGLView({
    isClassesEnabled,
}: {
    isClassesEnabled: boolean;
}): GeneralLedgerView {
    return {
        type: ViewType.GENERAL_LEDGER,
        columns: [
            { id: GeneralLedgerColumn.NAME, options: { pin: "left" as const } },
            { id: GeneralLedgerColumn.SOURCE },
            { id: GeneralLedgerColumn.REFERENCE },
            { id: GeneralLedgerColumn.DESCRIPTION },
            { id: GeneralLedgerColumn.COUNTERPARTY },
            ...(isClassesEnabled
                ? [{ id: GeneralLedgerColumn.CLASS as const }]
                : []),
            { id: GeneralLedgerColumn.SPLIT },
            {
                id: GeneralLedgerColumn.AMOUNT,
                options: { pin: "right" as const },
            },
            {
                id: GeneralLedgerColumn.BALANCE,
                options: { pin: "right" as const },
            },
        ],
        filters: {},
        options: {
            selection: false,
            bulkActions: false,
            density: ViewDensity.LG,
        },
    };
}

export function createGlDrilldownView(options: {
    isClassesEnabled: boolean;
}): GeneralLedgerView {
    const view = buildGLView(options);
    const columns = [...view.columns];
    const splitColumnIndex = columns.findIndex(
        ({ id }) => id === GeneralLedgerColumn.SPLIT,
    );
    columns.splice(splitColumnIndex + 1, 0, {
        id: GeneralLedgerColumn.EXCLUDE_FROM_1099,
    });
    return { ...view, columns };
}
